import React from 'react';
import EmptyState from '../oracle/EmptyState';
import BoneButton from '../oracle/BoneButton';
import CensorBar from '../oracle/CensorBar';
import { COPY } from '../../contracts/copyMap';
import { traitLabel } from '../../contracts/scanFeedbackApi';

// GIVEN — the judgments this member has given to other people.
//
// Deliberately a record, not a feed: no scores, no outcomes, no way back to
// the portrait. The rows exist so a user can see what they contributed, which
// is the other half of a system that shows them what they received.
//
// The portraits stay anonymous here exactly as they were in the deck. Nothing
// on a row identifies whose face it was — the callable does not return it.
function formatDate(iso) {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function VaultGivenView({ rows, onLoadMore, hasMore, loadingMore }) {
  if (!rows.length) {
    return (
      <EmptyState
        headline={COPY.FEEDBACK_GIVEN_EMPTY_HEADLINE}
        subtitle={COPY.FEEDBACK_GIVEN_EMPTY_BODY}
      />
    );
  }

  return (
    <div data-testid="vault-given">
      <ul className="list-none p-0 m-0 flex flex-col gap-4">
        {rows.map((row) => (
          <li
            key={row.feedback_id}
            className="flex gap-4 items-start bg-[#161615] border border-hairline-soft rounded-card p-4"
          >
            <div className="relative w-[64px] h-[85px] shrink-0 overflow-hidden bg-vein">
              {row.anonymized_image_url ? (
                <img
                  src={row.anonymized_image_url}
                  alt=""
                  aria-hidden="true"
                  className="w-full h-full object-cover"
                />
              ) : (
                // The portrait was withdrawn or deleted after the reading was
                // given. The reading still happened, so the row stays.
                <div className="w-full h-full flex items-center justify-center">
                  <CensorBar mode="ANONYMOUS" size="sm" />
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="font-display text-[11px] tracking-[0.16em] text-bone-dim">
                {formatDate(row.created_at)}
              </div>
              <TraitLine label={COPY.FEEDBACK_STRENGTHS_LABEL} keys={row.strengths} />
              <TraitLine label={COPY.FEEDBACK_IMPROVEMENTS_LABEL} keys={row.improvements} />
            </div>
          </li>
        ))}
      </ul>

      {hasMore && (
        <div className="mt-6 flex justify-center">
          <BoneButton onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? COPY.ANALYZING_LABEL : COPY.VAULT_LOAD_MORE_CTA}
          </BoneButton>
        </div>
      )}
    </div>
  );
}

function TraitLine({ label, keys }) {
  if (!keys || !keys.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="font-display uppercase text-[10px] tracking-[0.2em] text-bone-dim">
        {label}
      </span>
      <span className="font-display uppercase text-[11px] tracking-[0.12em] text-bone">
        {keys.map((k) => traitLabel(k)).join(' · ')}
      </span>
    </div>
  );
}
